import type { Message as StoredMessage } from '../../api/generated-api';
import type { ProxiedShareRecipients } from '../../api/overrides';
import type { ShareRecipient } from '../ShareViaEmailDialog';

/** HTTP status the share endpoint answers once a user's hourly allowance is spent. */
export const RATE_LIMIT_STATUS = 429;

/** HTTP status the proxy answers with when it could not confirm the email went out. */
export const SERVICE_UNAVAILABLE_STATUS = 503;

/** HTTP status a gateway answers with when the service behind it did not reply in time. */
export const GATEWAY_TIMEOUT_STATUS = 504;

/** Proxy error code for a send that may have reached the mail service. */
export const EMAIL_UNCONFIRMED_CODE = 'EMAIL_UNCONFIRMED';

/** RTK Query statuses for a request that went out but got no answer. */
const UNANSWERED_STATUSES: ReadonlySet<string> = new Set([
  'FETCH_ERROR',
  'TIMEOUT_ERROR',
]);

/** RTK Query status for an answer whose body could not be decoded. */
const PARSING_ERROR_STATUS = 'PARSING_ERROR';

/**
 * A share abandoned before `/share` was called, such as when the
 * stored messages could not be read or held no copy of the response,
 * so no email can have gone out.
 */
export class ShareNotSentError extends Error {
  /** What the stored-message lookup rejected with, if it rejected. */
  readonly lookupError: unknown;

  constructor(message: string, lookupError?: unknown) {
    super(message);
    this.name = 'ShareNotSentError';
    this.lookupError = lookupError;
  }
}

/**
 * Splits the chosen recipients the way the authorization proxy reads
 * them: directory picks by user id, so it checks the profile and reads
 * its current address, and typed entries by address.
 *
 * A directory pick whose id is its own address came from a profile
 * with no id; there is no user to name, so it is sent as an address.
 *
 * @param recipients - The recipients chosen in the dialog.
 * @returns The `recipients` field of the share request.
 */
export const toShareRecipients = (
  recipients: ShareRecipient[]
): Required<ProxiedShareRecipients> =>
  recipients.reduce<Required<ProxiedShareRecipients>>(
    (shareRecipients, { id, email, source }) => {
      if (source === 'directory' && id !== email) {
        shareRecipients.userIds.push(id);
      } else {
        shareRecipients.emails.push(email);
      }
      return shareRecipients;
    },
    { userIds: [], emails: [] }
  );

const hasErrorCode = (data: unknown, code: string): boolean =>
  typeof data === 'object' &&
  data !== null &&
  'errors' in data &&
  Array.isArray(data.errors) &&
  data.errors.some(
    (entry: unknown) =>
      typeof entry === 'object' &&
      entry !== null &&
      'errorCode' in entry &&
      entry.errorCode === code
  );

/**
 * The HTTP status a rejected request was answered with. RTK Query's
 * `unwrap()` rejects with `{ status, data }`, where `status` is the
 * HTTP code, or `'PARSING_ERROR'` with the code in `originalStatus`
 * when the body could not be decoded, such as a gateway's HTML page.
 */
const getAnsweredStatus = (error: unknown): number | undefined => {
  if (typeof error !== 'object' || error === null || !('status' in error)) {
    return undefined;
  }
  if (typeof error.status === 'number') {
    return error.status;
  }
  if (
    error.status === PARSING_ERROR_STATUS &&
    'originalStatus' in error &&
    typeof error.originalStatus === 'number'
  ) {
    return error.originalStatus;
  }
  return undefined;
};

const isSuccessStatus = (status: number): boolean =>
  status >= 200 && status < 300;

/**
 * Whether a rejected share may still have sent its email, so asking
 * the user to retry could mail the recipients twice.
 *
 * That holds when the request got no answer at all, when it was
 * accepted but the answer could not be read, when a gateway timed out
 * waiting on the service behind it, and when the proxy says the send
 * went unconfirmed. A {@link ShareNotSentError} never reached
 * `/share`, so it is not one.
 *
 * @param error - Whatever the share rejected with.
 * @returns `true` when the outcome of the send is unknown.
 */
export const isShareMaybeSentError = (error: unknown): boolean => {
  if (typeof error !== 'object' || error === null || !('status' in error)) {
    return false;
  }
  if (
    typeof error.status === 'string' &&
    UNANSWERED_STATUSES.has(error.status)
  ) {
    return true;
  }
  const status = getAnsweredStatus(error);
  if (status === undefined) {
    return false;
  }
  if (isSuccessStatus(status) || status === GATEWAY_TIMEOUT_STATUS) {
    return true;
  }
  return (
    status === SERVICE_UNAVAILABLE_STATUS &&
    'data' in error &&
    hasErrorCode(error.data, EMAIL_UNCONFIRMED_CODE)
  );
};

/**
 * Whether a rejected share was refused for exceeding the hourly limit,
 * whether the proxy or a gateway in front of it refused it.
 *
 * @param error - Whatever the share request rejected with.
 * @returns `true` only for a 429 response.
 */
export const isShareRateLimitError = (error: unknown): boolean =>
  getAnsweredStatus(error) === RATE_LIMIT_STATUS;

/** Milliseconds between a stored message and `target`; unknown times sort last. */
const distanceInTime = (message: StoredMessage, target: number): number => {
  const distance = Math.abs(Date.parse(message.createdAt ?? '') - target);
  return Number.isNaN(distance) ? Number.POSITIVE_INFINITY : distance;
};

const findClosestStoredResponse = (
  messages: StoredMessage[],
  content: string,
  target: number
): StoredMessage | undefined => {
  const expectedContent = content.trim();
  const matches = messages.filter(
    (message) =>
      message.role === 'assistant' &&
      message.id !== undefined &&
      message.id !== '' &&
      message.content?.trim() === expectedContent
  );
  if (matches.length === 0) {
    return undefined;
  }
  return matches.reduce((closest, message) =>
    distanceInTime(message, target) < distanceInTime(closest, target)
      ? message
      : closest
  );
};

/**
 * Finds the stored copy of an assistant response among a
 * conversation's stored messages.
 *
 * Matches on the response's text. When several stored answers carry
 * the same text, the one created closest to `createdAt` wins, so each
 * bubble resolves to its own turn; with no usable times the newest
 * match does.
 *
 * @param messages - Stored messages, newest first.
 * @param content - The response's raw text.
 * @param createdAt - When the response arrived (ISO 8601).
 * @returns The stored copy, or `undefined` when none matches.
 */
export const findStoredResponse = (
  messages: StoredMessage[],
  content: string,
  createdAt: string
): StoredMessage | undefined =>
  findClosestStoredResponse(messages, content, Date.parse(createdAt));

/**
 * Whether the newest stored messages read so far already hold the copy
 * {@link findStoredResponse} would pick from the whole history.
 *
 * Every unread message is older than everything read, so it lies at
 * least as far from `createdAt` as the oldest time read; once that gap
 * is no smaller than the best match's, no unread copy can beat it.
 *
 * @param read - The stored messages read so far, newest first.
 * @param content - The response's raw text.
 * @param createdAt - When the response arrived (ISO 8601).
 * @returns `true` when reading older messages cannot change the pick.
 */
export const hasReadClosestStoredResponse = (
  read: StoredMessage[],
  content: string,
  createdAt: string
): boolean => {
  const target = Date.parse(createdAt);
  const closest = findClosestStoredResponse(read, content, target);
  if (closest === undefined) {
    return false;
  }
  if (Number.isNaN(target)) {
    return true;
  }
  const oldestRead = Math.min(
    ...read
      .map((message) => Date.parse(message.createdAt ?? ''))
      .filter((time) => !Number.isNaN(time))
  );
  const nearestUnread = Math.max(0, target - oldestRead);
  return nearestUnread >= distanceInTime(closest, target);
};
